/**
 * TenantContext und Actor nach `04` und `01` §1.12.
 *
 * Zur Begriffswahl: `01` §1.12 unterscheidet **Kundenstack** (Betriebssicht),
 * **Mandant** (der Kunde als Rechtssubjekt) und **Tenant** (der technische
 * Bezeichner). Im Code heisst es durchgaengig `tenantId` - genau ein Wort je
 * Begriff, damit nicht spaeter drei Klassennamen fuer dieselbe Sache
 * entstehen.
 */

/** Rollen aus `01` §1.6. Zentral benannt, dienstspezifische leiten sich ab. */
export const ROLLE = [
  "tenant-admin",
  "office",
  "dispatcher",
  "technician",
  "accounting",
  "external-guest",
  "external-partner",
] as const;

export type Rolle = (typeof ROLLE)[number];

/**
 * Wer handelt. Ein Actor ist entweder ein Mensch, ein Service-Account oder
 * ein Externer ohne Konto.
 *
 * Die Unterscheidung ist nicht kosmetisch: `05` §5.15 macht
 * Service-Account-Aktionen und Admin-Impersonation getrennt auditpflichtig,
 * und das geht nur, wenn der Typ am Actor haengt statt erraten zu werden.
 */
export type ActorArt = "user" | "service" | "magiclink";

export interface Actor {
  readonly art: ActorArt;
  /** ResourceRef auf die Identität; bei `magiclink` die Kennung des Links. */
  readonly ref: string;
  readonly rollen: readonly Rolle[];
  /**
   * Gesetzt, wenn ein Betreiber im Namen eines Benutzers handelt.
   * Auditpflichtig nach `05` §5.15 - deshalb ein eigenes Feld und kein
   * Vermerk im Freitext.
   */
  readonly imAuftragVon?: string;
}

export interface TenantContext {
  readonly tenantId: string;
  readonly actor: Actor;
  /**
   * Verkettet zusammengehörende Vorgänge über Dienstgrenzen hinweg. Ohne
   * ihn lässt sich ein Fehler im Portal nicht der auslösenden Nachricht in
   * Stalwart zuordnen.
   */
  readonly correlationId: string;
}

/** Prüft, ob ein Actor mindestens eine der geforderten Rollen trägt. */
export function hatRolle(actor: Actor, ...gefordert: readonly Rolle[]): boolean {
  return gefordert.some((r) => actor.rollen.includes(r));
}

/**
 * `external-guest` und `external-partner` dürfen nach `01` §1.6 nicht
 * vermischt werden: Ein Gast ist ein Endkunde ohne Konto mit befristetem
 * MagicLink, ein Partner hat ein eigenes Konto mit unbefristetem, aber
 * widerrufbarem Leserecht - typischerweise der Steuerberater.
 *
 * Die Funktion existiert, damit die Unterscheidung eine Stelle im Code hat
 * und nicht an jedem Aufrufort neu formuliert wird.
 */
export function istExtern(actor: Actor): boolean {
  return hatRolle(actor, "external-guest", "external-partner");
}
